/**
 * Glucose - AI-Powered Diabetes Management Assistant
 * Providing friendly, knowledgeable guidance for diabetes management
 * Based on comprehensive information from Diabetes Australia
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Typography,
  Button,
} from "@mui/material";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import { getCurrentUser } from "aws-amplify/auth";
import { getCognitoUserWithAttributes } from "./utils/cognitoHelper";

// Import custom password reset component
import PasswordResetFlow from "./components/auth/PasswordResetFlow";

import appConfig, { getAmplifyConfiguration } from "./config/config";
import { formFields } from "./config/amplifyConfig";
import {
  useConfig,
  useThemeConfig,
  useFeatureFlags,
} from "./components/ConfigProvider";
import apiService from "./services/apiService";
import ErrorBoundary from "./components/ErrorBoundary";
import AccessibilityFixer from "./components/AccessibilityFixer";
import LoadingFallback from "./components/LoadingFallback";

// Lazy load components for better performance
const ChatInterface = React.lazy(() => import("./components/ChatInterface"));
const Sidebar = React.lazy(() => import("./components/Sidebar"));
const UserProfile = React.lazy(() => import("./pages/UserProfile"));
const EarlyAccessAlert = React.lazy(
  () => import("./components/EarlyAccessAlert"),
);
const FeedbackAlert = React.lazy(() => import("./components/FeedbackAlert"));
const OnboardingRouter = React.lazy(
  () => import("./components/onboarding/OnboardingRouter"),
);
const ToolsCenter = React.lazy(() => import("./components/tools/ToolsCenter"));

// Development testing utilities
if (process.env.NODE_ENV === "development") {
  import("./utils/localhostTest")
    .then(() => {
      console.log(
        "🔬 [LOCALHOST] Testing utilities loaded - check browser console for commands",
      );
    })
    .catch((error) => {
      console.warn(
        "⚠️ [LOCALHOST] Could not load testing utilities:",
        error.message,
      );
    });

  // Load WebSocket authentication test utilities
  import("./test/websocket-auth-test")
    .then(() => {
      console.log("🔌 [WEBSOCKET] Authentication test utilities loaded");
    })
    .catch((error) => {
      console.warn(
        "⚠️ [WEBSOCKET] Could not load test utilities:",
        error.message,
      );
    });

  // Add WebSocket debugging utilities
  import("./services/simpleWebSocketChat")
    .then((module) => {
      const simpleWebSocketChat = module.default;

      // Global debugging functions
      window.debugWebSocket = () => {
        const status = simpleWebSocketChat.getConnectionStatus();
        console.log("🔍 WebSocket Connection Status:", status);
        return status;
      };

      window.forceWebSocketCleanup = () => {
        simpleWebSocketChat.forceCleanup();
        console.log("✅ WebSocket force cleanup completed");
      };

      window.testConnectionStability = async () => {
        console.log("🧪 Testing WebSocket connection stability...");

        for (let i = 0; i < 5; i++) {
          console.log(`Test ${i + 1}: Checking connection...`);
          const status = simpleWebSocketChat.getConnectionStatus();
          console.log(`  - Connected: ${status.isConnected}`);
          console.log(`  - Handlers: ${status.eventHandlers.length}`);
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        console.log("✅ Connection stability test complete");
        return simpleWebSocketChat.getConnectionStatus();
      };

      console.log("🔧 [WEBSOCKET] Debugging utilities loaded");
    })
    .catch((error) => {
      console.warn(
        "⚠️ [WEBSOCKET] Could not load debugging utilities:",
        error.message,
      );
    });

  // Add alert testing utilities
  window.resetEarlyAccessAlert = () => {
    sessionStorage.removeItem("glucose_early_access_alert_shown");
    sessionStorage.removeItem("glucose_early_access_alert_timestamp");
    console.log(
      "🔔 Early Access Alert reset - will show on next app ready state",
    );

    // Force reload the alert logic if we're currently in the app
    if (window.APP_DEBUG?.appReady && window.APP_DEBUG?.user) {
      console.log("🔔 App is ready, triggering immediate alert check...");
      window.dispatchEvent(new CustomEvent("force-show-early-access"));
    }
  };

  window.resetFeedbackAlert = () => {
    localStorage.removeItem("glucose_feedback_alert_dismissed");
    console.log("🔔 Feedback Alert reset - timer will restart");
  };

  window.showEarlyAccessAlert = () => {
    window.dispatchEvent(new CustomEvent("force-show-early-access"));
    console.log("🔔 Forcing Early Access Alert to show");
  };

  window.checkEarlyAccessAlert = () => {
    const hasSeenAlert = sessionStorage.getItem(
      "glucose_early_access_alert_shown",
    );
    const alertTimestamp = sessionStorage.getItem(
      "glucose_early_access_alert_timestamp",
    );
    console.log("🔔 Early Access Alert session storage:", {
      hasSeenAlert: !!hasSeenAlert,
      alertTimestamp,
      timestampDate: alertTimestamp
        ? new Date(parseInt(alertTimestamp)).toISOString()
        : "N/A",
    });
    console.log("🔔 Current alert state variables:", {
      appReady: window.APP_DEBUG?.appReady,
      hasUser: !!window.APP_DEBUG?.user,
      showEarlyAccessAlert: window.APP_DEBUG?.showEarlyAccessAlert,
    });
    return !hasSeenAlert;
  };

  window.forceShowEarlyAccessAlert = () => {
    console.log("🔔 Forcing Early Access Alert to show immediately");
    window.dispatchEvent(new CustomEvent("force-show-early-access"));
    return true;
  };

  window.debugEarlyAccessAlert = () => {
    const hasSeenAlert = sessionStorage.getItem(
      "glucose_early_access_alert_shown",
    );
    const alertTimestamp = sessionStorage.getItem(
      "glucose_early_access_alert_timestamp",
    );
    const debug = window.APP_DEBUG || {};

    console.log("🔔 === EARLY ACCESS ALERT DEBUG REPORT ===");
    console.log("🔔 Session Storage:", {
      hasSeenAlert: !!hasSeenAlert,
      alertTimestamp,
      timestampDate: alertTimestamp
        ? new Date(parseInt(alertTimestamp)).toISOString()
        : "N/A",
    });
    console.log("🔔 App State:", debug);
    console.log(
      "🔔 Should Show Alert:",
      !hasSeenAlert && debug.appReady && debug.user,
    );
    console.log("🔔 === END DEBUG REPORT ===");

    return {
      hasSeenAlert: !!hasSeenAlert,
      alertTimestamp,
      appState: debug,
      shouldShow: !hasSeenAlert && debug.appReady && debug.user,
    };
  };

  console.log("🔔 Alert testing utilities available:");
  console.log(
    "   - resetEarlyAccessAlert() - Reset early access alert & trigger if ready",
  );
  console.log("   - resetFeedbackAlert() - Reset feedback alert timer");
  console.log("   - showEarlyAccessAlert() - Force show early access alert");
  console.log("   - forceShowEarlyAccessAlert() - Force show immediately");
  console.log("   - checkEarlyAccessAlert() - Check if alert should show");
  console.log("   - debugEarlyAccessAlert() - Complete debug report");
  console.log("🔌 WebSocket testing utilities:");
  console.log("   - testWebSocketAuth() - Test token retrieval and format");
  console.log("   - testWebSocketConnection() - Test full connection flow");
  console.log("   - runAllWebSocketTests() - Run complete test suite");
  console.log("🔧 WebSocket debugging utilities:");
  console.log(
    "   - window.debugWebSocket() - Show WebSocket connection status",
  );
  console.log(
    "   - window.forceWebSocketCleanup() - Force cleanup all connections",
  );
  console.log(
    "   - window.testConnectionStability() - Test connection stability",
  );

  // Only reset for fresh deployment if explicitly called
  window.resetForDeployment = () => {
    sessionStorage.removeItem("glucose_early_access_alert_shown");
    sessionStorage.removeItem("glucose_early_access_alert_timestamp");
    localStorage.removeItem("glucose_feedback_alert_dismissed");
    console.log("🔔 All alerts reset for fresh deployment testing");
  };

  console.log("   - resetForDeployment() - Reset all alerts for fresh testing");
  console.log("   - window.APP_DEBUG - Inspect current state variables");

  // For immediate debugging of the current issue
  console.log("🔔 === IMMEDIATE DEBUG ===");
  console.log("🔔 Current session storage:");
  console.log(
    "   - glucose_early_access_alert_shown:",
    sessionStorage.getItem("glucose_early_access_alert_shown"),
  );
  console.log(
    "   - glucose_early_access_alert_timestamp:",
    sessionStorage.getItem("glucose_early_access_alert_timestamp"),
  );
  console.log(
    "🔔 TO FIX CURRENT ISSUE: Run resetEarlyAccessAlert() in console",
  );
  console.log("🔔 === END IMMEDIATE DEBUG ===");
}

// Configure Amplify with simplified configuration
const configureAmplify = () => {
  try {
    const config = getAmplifyConfiguration();
    Amplify.configure(config);
  } catch (error) {
    console.error("❌ Failed to configure Amplify:", error);
    throw error;
  }
};

// Initialize Amplify configuration immediately (synchronous)
try {
  configureAmplify();
} catch (error) {
  console.error("❌ Critical: Amplify configuration failed completely:", error);
}

// Add global styles for blue login background and prevent scaling issues
const globalStyles = `
  *:not(.MuiTooltip-popper):not(.MuiTooltip-tooltip):not(.MuiTooltip-arrow):not([class*="MuiPopper"]):not([class*="MuiTooltip"]) {
    transform: none !important;
    zoom: 1 !important;
    -webkit-transform: none !important;
    -moz-transform: none !important;
    -ms-transform: none !important;
    -webkit-zoom: 1 !important;
    -moz-zoom: 1 !important;
  }
  
  html, body {
    zoom: 1 !important;
    transform: none !important;
    font-size: 16px !important;
    overflow-x: hidden;
    width: 100% !important;
    height: 100% !important;
  }
  
  #root {
    width: 100% !important;
    height: 100% !important;
    transform: none !important;
    zoom: 1 !important;
  }
  
  .MuiBox-root:not(.MuiTooltip-popper):not([class*="MuiPopper"]) {
    transform: none !important;
    zoom: 1 !important;
  }
  
  /* Allow tooltips and poppers to use transforms for positioning */
  .MuiTooltip-popper,
  .MuiTooltip-tooltip,
  .MuiTooltip-arrow,
  [class*="MuiPopper"],
  [class*="MuiTooltip"] {
    transform: unset !important;
    -webkit-transform: unset !important;
    -moz-transform: unset !important;
    -ms-transform: unset !important;
  }
  
  .amplify-authenticator {
    background-color: #005EB8 !important;
  }
  .amplify-tabs {
    background-color: #005EB8 !important;
  }
  .amplify-authenticator__modal {
    background-color: #005EB8 !important;
  }
  .amplify-card {
    background-color: rgba(255, 255, 255, 0.1) !important;
    backdrop-filter: blur(10px);
  }
  body .amplify-authenticator {
    background: #005EB8 !important;
  }
  
  /* Authenticator Tab Styling */
  .amplify-tabs__list {
    display: flex !important;
    background: transparent !important;
    border-bottom: none !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
    gap: 2px !important;
  }
  
  .amplify-tabs__item {
    flex: 1 !important;
    background: #f3f4f6 !important;
    color: #555 !important;
    border: none !important;
    border-radius: 12px 12px 0 0 !important;
    padding: 16px 24px !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    margin: 0 !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    border-bottom: 3px solid transparent !important;
    position: relative !important;
    z-index: 2 !important;
  }
  
  .amplify-tabs__item--active,
  .amplify-tabs__item[aria-selected="true"] {
    background: white !important;
    color: #1a1a1a !important;
    font-weight: 600 !important;
    border-bottom: 3px solid transparent !important;
    border-top: 3px solid #005EB8 !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    z-index: 3 !important;
  }
  
  .amplify-tabs__item:hover:not(.amplify-tabs__item--active):not([aria-selected="true"]) {
    background: #e5e7eb !important;
    color: #374151 !important;
  }
  
  /* Ensure tab content area connects seamlessly with active tab */
  .amplify-tabs__panel {
    background: white !important;
    border-radius: 0 0 12px 12px !important;
    padding: 24px !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    margin-top: -1px !important;
    position: relative !important;
    z-index: 1 !important;
  }
  
  /* Remove any gaps between tabs and panel */
  .amplify-tabs {
    margin-bottom: 0 !important;
  }
  
  /* Footer positioning fix - only apply when not on auth screens */
  .app-main-content .MuiBox-root.css-1m2h7ts {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 1000 !important;
    background: #005EB8 !important;
    padding: 12px 16px !important;
    text-align: center !important;
    border: none !important;
    border-top: none !important;
    box-shadow: none !important;
  }
  
  /* Footer text color fix */
  .app-main-content .MuiBox-root.css-1m2h7ts .MuiTypography-root {
    color: white !important;
  }
  
  /* Remove all fixed positioning from auth screens */
  .amplify-authenticator .MuiBox-root {
    position: static !important;
  }
  
  /* Hide the problematic copyright banner on auth screens */
  .amplify-authenticator .MuiBox-root.css-1m2h7ts {
    display: none !important;
  }
  
  /* Add copyright to the bottom of the auth area */
  .amplify-authenticator::after {
    content: "Copyright © 2025 glucose.ai - All Rights Reserved";
    display: block;
    text-align: center;
    padding: 16px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.75rem;
    background: #005EB8;
    margin-top: 24px;
  }
`;

// Inject global styles
if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("amplify-blue-bg");
  if (!existingStyle) {
    const style = document.createElement("style");
    style.id = "amplify-blue-bg";
    style.textContent = globalStyles;
    document.head.appendChild(style);
  }
}

// Import password requirements component
const PasswordRequirements = React.lazy(
  () => import("./components/PasswordRequirements"),
);
const AmplifyPasswordEnhancer = React.lazy(
  () => import("./components/AmplifyPasswordEnhancer"),
);

// Custom component for legal links that gets injected into forms
const LegalLinksComponent = () => (
  <Box
    sx={{
      mt: 2,
      mb: 2,
      textAlign: "center",
      padding: "12px",
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderRadius: "8px",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    }}
  >
    <Typography
      variant="body2"
      sx={{
        color: "#005EB8",
        mb: 1,
        fontSize: "0.85rem",
        fontWeight: 500,
      }}
    >
      By continuing, you agree to our:
    </Typography>
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: { xs: 1, sm: 3 },
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
      }}
    >
      <Typography
        component="a"
        href="https://glucose.ai/terms"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "#4169e1",
          textDecoration: "underline",
          fontSize: "0.8rem",
          fontWeight: 600,
          "&:hover": {
            color: "#1e3a8a",
            textDecoration: "underline",
          },
        }}
      >
        Terms & Conditions
      </Typography>
      <Typography
        component="a"
        href="https://glucose.ai/privacy"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: "#4169e1",
          textDecoration: "underline",
          fontSize: "0.8rem",
          fontWeight: 600,
          "&:hover": {
            color: "#1e3a8a",
            textDecoration: "underline",
          },
        }}
      >
        Privacy Policy
      </Typography>
    </Box>
  </Box>
);

// glucose AI diabetes-focused authenticator components
const authenticatorComponents = {
  Header() {
    return (
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
          background: "#005EB8",
          color: "white",
          py: 2,
        }}
      >
        <Box
          sx={{
            height: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <img
            src="/glucose_logo.png"
            alt="glucose Diabetes AI Assistant"
            style={{
              height: "120px",
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
              display: "block",
            }}
            onError={(e) => {
              console.warn("Login logo not found, using text fallback");
              e.target.style.display = "none";
              const fallback = document.getElementById("login-logo-fallback");
              if (fallback) {
                fallback.style.display = "block";
              }
            }}
          />
          <Typography
            variant="h3"
            sx={{
              color: "white",
              fontWeight: 700,
              display: "none",
            }}
            id="login-logo-fallback"
          >
            glucose
          </Typography>
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "white",
            mb: 1,
          }}
        >
          Diabetes AI Assistant
        </Typography>
        <Typography
          variant="h6"
          sx={{
            opacity: 0.9,
            color: "white",
            fontWeight: 400,
          }}
        >
          Your diabetes management companion
        </Typography>
        <Typography
          variant="body2"
          sx={{
            opacity: 0.8,
            color: "white",
            mt: 1,
            fontStyle: "italic",
          }}
        >
          Made for the 5.5% of Australians
        </Typography>
      </Box>
    );
  },

  SignIn: {
    Footer() {
      return (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Button
              variant="text"
              onClick={() => {
                console.log("🔐 Password reset button clicked...");
                if (window.openPasswordReset) {
                  window.openPasswordReset();
                } else {
                  console.error("❌ Password reset function not available");
                }
              }}
              sx={{
                color: "#005EB8 !important",
                textDecoration: "underline",
                fontWeight: 500,
                fontSize: "0.9rem",
                "&:hover": {
                  backgroundColor: "rgba(80, 134, 237, 0.1)",
                  textDecoration: "underline",
                  color: "#005EB8 !important",
                },
                "&:focus": {
                  color: "#005EB8 !important",
                  backgroundColor: "rgba(80, 134, 237, 0.15)",
                },
              }}
            >
              Forgot your password?
            </Button>
          </Box>
          <LegalLinksComponent />
        </Box>
      );
    },
  },

  SignUp: {
    Footer() {
      return (
        <Box sx={{ mt: 2 }}>
          <LegalLinksComponent />
        </Box>
      );
    },
  },

  Footer() {
    return (
      <Box
        sx={{
          mt: 2,
          py: 2,
          textAlign: "center",
          background: "#005EB8",
          color: "white",
        }}
      >
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
          Data & LLM is stored, processed, and remains onshore in Australia
        </Typography>
      </Box>
    );
  },
};

function App() {
  // Use configuration from context
  const { config } = useConfig();
  const themeConfig = useThemeConfig();
  const featureFlags = useFeatureFlags();

  // Core state
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [user, setUser] = useState(null);

  // Loading states
  const [authChecking, setAuthChecking] = useState(true);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);

  // App state
  const [appReady, setAppReady] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [notification, setNotification] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showEarlyAccessAlert, setShowEarlyAccessAlert] = useState(false);
  const [showFeedbackAlert, setShowFeedbackAlert] = useState(false);

  // Password reset state
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  // Refs
  const initializationInProgress = useRef(false);
  const feedbackTimerRef = useRef(null);

  // REMOVED: Keep-alive mechanism (part of warmup complexity)

  // Password reset handler - make it globally accessible
  const handlePasswordReset = useCallback(() => {
    console.log("🔐 Password reset requested, showing custom flow...");
    console.log("🔐 Current showPasswordReset state:", showPasswordReset);
    setShowPasswordReset(true);
    console.log("🔐 Setting showPasswordReset to true...");
  }, []);

  // Make the password reset function globally accessible
  useEffect(() => {
    window.openPasswordReset = handlePasswordReset;
    return () => {
      delete window.openPasswordReset;
    };
  }, [handlePasswordReset]);

  // Load user sessions (for sidebar display only - don't change current session)
  const loadSessions = useCallback(async () => {
    try {
      console.log("📂 Loading chat sessions for sidebar...");
      const response = await apiService.getChatSessions();

      // Session response received - no sensitive data logged

      const sessionList = response.sessions || response || [];

      // Sort sessions by last activity (most recent first)
      const sortedSessions = sessionList.sort(
        (a, b) =>
          new Date(b.lastActivity || b.updatedAt || b.timestamp) -
          new Date(a.lastActivity || a.updatedAt || a.timestamp),
      );

      // Update sessions list but preserve current session
      setSessions((prev) => {
        // If we already have sessions (including the new one), merge them intelligently
        if (prev.length > 0) {
          const existingIds = new Set(prev.map((s) => s.sessionId));
          const newSessions = sortedSessions.filter(
            (s) => !existingIds.has(s.sessionId),
          );
          const merged = [...prev, ...newSessions];
          return merged;
        }
        return sortedSessions;
      });
    } catch (error) {
      console.log(
        "⚠️ Failed to load previous sessions for sidebar:",
        error.message,
      );
      console.log("🔍 Session loading error details:", error);
      // Don't change anything if loading fails
    }
  }, []);

  // SIMPLIFIED: Direct session loading (no warmup complexity)
  const loadInitialSession = useCallback(async () => {
    try {
      // Load or create session (simple, direct)
      const sessionResult = await apiService.createChatSession(false);
      const sessionData = sessionResult.session || sessionResult;

      // Set the session as current
      const sessionUpdate = {
        sessionId: sessionData.sessionId,
        timestamp:
          sessionData.timestamp ||
          sessionData.createdAt ||
          new Date().toISOString(),
        lastActivity:
          sessionData.lastActivity ||
          sessionData.updatedAt ||
          new Date().toISOString(),
        messageCount: sessionData.messageCount || 0,
        status: sessionData.status || "active",
        title:
          sessionData.title ||
          `Chat ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
      };

      setSessions([sessionUpdate]);
      setCurrentSession(sessionUpdate);
      console.log("✅ Session set as current and ready for conversation");

      // Load all previous sessions for sidebar (background)
      loadSessions().catch(() => {});
    } catch (sessionError) {
      setSessions([]);
      setCurrentSession(null);
    }
  }, [loadSessions]);

  // Initial authentication check with WebSocket cleanup
  const checkInitialAuth = useCallback(async () => {
    try {
      // Import SimpleChatLogic dynamically to avoid circular dependencies
      const { default: simpleChatLogic } =
        await import("./services/SimpleChatLogic");

      // Clear any stale WebSocket connections from previous session
      simpleChatLogic.disconnect(true);

      // Clear stale sessions to ensure fresh start
      setSessions([]);
      setCurrentSession(null);

      // Add small delay to show loading
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Fetch complete user info with Cognito attributes
      const completeUser = await getCognitoUserWithAttributes();
      setUser(completeUser);
    } catch (error) {
      // User not authenticated - this is fine, login screen will show
    } finally {
      setAuthChecking(false);
    }
  }, []);

  // Check if user needs onboarding (uses existing API)
  const checkOnboardingStatus = useCallback(async () => {
    if (!user) return;

    setCheckingOnboarding(true);

    try {
      const profile = await apiService.getUserProfile();

      // For Glucose app: Check if user has diabetesType set (required for onboarding completion)
      // Also check nested personalInfo.diabetesInfo for backwards compatibility
      const hasDiabetesType = profile && (
        profile.diabetesType || 
        profile.personalInfo?.diabetesType ||
        profile.personalInfo?.diabetesInfo?.diabetesType
      );
      
      if (hasDiabetesType) {
        console.log("User has diabetes type set, skipping onboarding");
        setNeedsOnboarding(false);
      } else {
        console.log("No diabetes type found, showing onboarding");
        setNeedsOnboarding(true);
      }
    } catch (error) {
      // New user or error loading profile - show onboarding
      console.log("Error loading profile, showing onboarding:", error.message);
      setNeedsOnboarding(true);
    } finally {
      setCheckingOnboarding(false);
    }
  }, [user]);

  // SIMPLIFIED: Initialize app after authentication (no warmup complexity)
  const initializeApp = useCallback(async () => {
    // Prevent multiple initializations
    if (initializationInProgress.current) {
      console.log("🔄 Initialization already in progress, skipping...");
      return;
    }

    initializationInProgress.current = true;

    try {
      // Load initial session (simple, direct)
      await loadInitialSession();
    } catch (error) {
      console.error("❌ Initialization error:", error);

      setNotification({
        open: true,
        message: "App loaded successfully. Chat is ready to use.",
        severity: "info",
      });
    } finally {
      setAppReady(true);
      initializationInProgress.current = false;
    }
  }, [loadInitialSession]);

  // Session handlers
  const handleSessionSelect = (session) => {
    setCurrentSession(session);
  };

  const handleSessionCreate = (newSession) => {
    setSessions((prev) => [newSession, ...prev]);
    setCurrentSession(newSession);
  };

  const handleSessionUpdate = (updatedSession) => {
    // Handle null/undefined session (session clearing)
    if (!updatedSession) {
      setCurrentSession(null);
      return;
    }

    setSessions((prev) => {
      const existingIndex = prev.findIndex(
        (s) => s.sessionId === updatedSession.sessionId,
      );
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...updatedSession,
        };
        return updated;
      } else {
        return [updatedSession, ...prev];
      }
    });

    // Only update currentSession if it's the same session or no current session
    if (
      !currentSession ||
      currentSession.sessionId === updatedSession.sessionId
    ) {
      setCurrentSession(updatedSession);
    }
  };

  const handleSessionDelete = (sessionId) => {
    console.log("🗑️ Deleting session:", sessionId);
    setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    if (currentSession?.sessionId === sessionId) {
      setCurrentSession(null);
    }
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Handle Early Access Alert
  const handleEarlyAccessAlertClose = () => {
    console.log("🔔 Closing Early Access Alert");
    setShowEarlyAccessAlert(false);
    // Remember that user has seen the alert for this session with timestamp
    const timestamp = Date.now().toString();
    sessionStorage.setItem("glucose_early_access_alert_shown", "true");
    sessionStorage.setItem(
      "glucose_early_access_alert_timestamp",
      timestamp,
    );
    console.log(
      "🔔 Early Access Alert marked as shown in session storage with timestamp:",
      timestamp,
    );
  };

  // Early Access Alert state tracking
  useEffect(() => {
    // State change tracked - no verbose logging needed

    // Update debug object for console inspection
    if (process.env.NODE_ENV === "development") {
      window.APP_DEBUG = {
        appReady,
        user: !!user,
        showEarlyAccessAlert,
        showFeedbackAlert,
        authChecking,
      };
    }
  }, [showEarlyAccessAlert, appReady, user, showFeedbackAlert, authChecking]);

  // Handle Feedback Alert
  const handleFeedbackAlertClose = () => {
    setShowFeedbackAlert(false);
    // Remember that user has permanently dismissed the feedback alert
    localStorage.setItem("glucose_feedback_alert_dismissed", "true");
    // Clear any running timer
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  };

  const handleFeedbackAlertNotNow = () => {
    setShowFeedbackAlert(false);
    // Reset the 5-minute timer
    startFeedbackTimer();
  };

  const startFeedbackTimer = useCallback(() => {
    // Clear any existing timer
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
    }

    // Set new 5-minute timer
    feedbackTimerRef.current = setTimeout(
      () => {
        // Check if user hasn't permanently dismissed the alert
        const hasDismissed = localStorage.getItem(
          "glucose_feedback_alert_dismissed",
        );
        if (!hasDismissed) {
          setShowFeedbackAlert(true);
        }
      },
      5 * 60 * 1000,
    ); // 5 minutes
  }, []);

  // Show Early Access Alert after user logs in (with delay and session check)
  useEffect(() => {
    if (appReady && user) {
      // Check if user has already seen the alert in this session
      const hasSeenAlert = sessionStorage.getItem(
        "glucose_early_access_alert_shown",
      );
      const alertTimestamp = sessionStorage.getItem(
        "glucose_early_access_alert_timestamp",
      );
      const currentTime = Date.now();

      // Reset if alert was shown more than 24 hours ago (for testing and development)
      let shouldReset = false;
      if (hasSeenAlert && alertTimestamp) {
        const timeDiff = currentTime - parseInt(alertTimestamp);
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        if (hoursDiff > 24) {
          shouldReset = true;
        }
      }

      if (!hasSeenAlert || shouldReset) {
        if (shouldReset) {
          sessionStorage.removeItem("glucose_early_access_alert_shown");
          sessionStorage.removeItem(
            "glucose_early_access_alert_timestamp",
          );
        }

        // Show alert after 3-second delay to ensure everything is loaded
        const alertTimer = setTimeout(() => {
          setShowEarlyAccessAlert(true);
        }, 3000);

        return () => {
          clearTimeout(alertTimer);
        };
      } else if (process.env.NODE_ENV === "development") {
        console.log(
          "🔔 Early Access Alert: To reset, run resetEarlyAccessAlert()",
        );
      }
    }
  }, [appReady, user]);

  // Force show event listener for development
  useEffect(() => {
    const forceShowHandler = () => {
      console.log("🔔 Force showing Early Access Alert via event");
      setShowEarlyAccessAlert(true);
    };

    window.addEventListener("force-show-early-access", forceShowHandler);

    return () => {
      window.removeEventListener("force-show-early-access", forceShowHandler);
    };
  }, []);

  // Start Feedback Alert timer when app is ready
  useEffect(() => {
    if (appReady && user) {
      // Check if user has permanently dismissed the feedback alert
      const hasDismissed = localStorage.getItem(
        "glucose_feedback_alert_dismissed",
      );

      if (!hasDismissed) {
        // Start the 5-minute timer
        startFeedbackTimer();
      }
    }

    // Cleanup timer on unmount
    return () => {
      if (feedbackTimerRef.current) {
        clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = null;
      }
    };
  }, [appReady, user, startFeedbackTimer]);

  // Initial authentication check
  useEffect(() => {
    checkInitialAuth();
  }, [checkInitialAuth]);

  // Check onboarding status when user is authenticated
  useEffect(() => {
    if (user && !checkingOnboarding) {
      checkOnboardingStatus();
    }
  }, [user, checkOnboardingStatus]);

  // Initialize app only after onboarding check complete and not needed
  useEffect(() => {
    // Only initialize if:
    // 1. User is authenticated
    // 2. Not already checking onboarding
    // 3. Onboarding check is complete (needsOnboarding is false)
    // 4. App is not already ready
    // 5. Not already initializing

    if (
      user &&
      !checkingOnboarding &&
      !needsOnboarding &&
      !appReady &&
      !initializationInProgress.current
    ) {
      initializeApp();
    }
  }, [user, checkingOnboarding, needsOnboarding, appReady, initializeApp]);

  // Create theme based on configuration
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "light",
          primary: {
            main: themeConfig.primary || "#005EB8",
            light: "#7ba4f0",
            dark: "#3b67c9",
            contrastText: "#ffffff",
          },
          secondary: {
            main: themeConfig.secondary || "#FFB300",
            light: "#FFC107",
            dark: "#FF8F00",
            contrastText: "#000000",
          },
          background: {
            default: themeConfig.background || "#F8F9FA",
            paper: "#ffffff",
          },
          text: {
            primary: themeConfig.textPrimary || "#1A1A1A",
            secondary: themeConfig.textSecondary || "#666666",
          },
          info: {
            main: "#0277BD",
            light: "#03A9F4",
            dark: "#01579B",
          },
        },
        typography: {
          fontFamily:
            themeConfig.fontFamily ||
            "'Open Sans', sans-serif",
          h1: { fontWeight: 700, color: themeConfig.primary || "#005EB8" },
          h2: { fontWeight: 600, color: themeConfig.primary || "#005EB8" },
          h3: { fontWeight: 600, color: themeConfig.primary || "#005EB8" },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
          body1: {
            lineHeight: 1.6,
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 12,
                padding: "8px 20px",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "0 2px 8px rgba(80, 134, 237, 0.25)",
                  transform: "translateY(-1px)",
                },
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontWeight: 500,
                borderRadius: 16,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
                "&:hover": {
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                },
              },
            },
          },
          MuiAvatar: {
            styleOverrides: {
              root: {
                boxShadow: "0 2px 8px rgba(80, 134, 237, 0.25)",
              },
            },
          },
        },
      }),
    [themeConfig],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AccessibilityFixer />
      <ErrorBoundary>
        <Box
          sx={{
            background: "#005EB8 !important",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .amplify-authenticator": {
              background: "#005EB8 !important",
            },
            "& .amplify-tabs": {
              background: "#005EB8 !important",
            },
            "& .amplify-authenticator__modal": {
              background: "#005EB8 !important",
            },
          }}
        >
          {/* Initial loading screen (during auth check) */}
          {authChecking && (
            <Box textAlign="center" sx={{ maxWidth: 400, px: 3 }}>
              <Box
                sx={{
                  mb: 4,
                  height: "120px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src="/glucose_logo.png"
                  alt="glucose Diabetes AI Assistant"
                  style={{
                    height: "120px",
                    width: "auto",
                    maxWidth: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                  onError={(e) => {
                    console.warn("Loading logo not found, using text fallback");
                    e.target.style.display = "none";
                    const fallback = document.getElementById(
                      "loading-logo-fallback",
                    );
                    if (fallback) {
                      fallback.style.display = "block";
                    }
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    display: "none",
                  }}
                  id="loading-logo-fallback"
                >
                  glucose
                </Typography>
              </Box>
              <CircularProgress
                size={48}
                sx={{
                  mb: 3,
                  color: "white",
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: "white",
                }}
              >
                Glucose Diabetes AI Assistant
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 2,
                  color: "white",
                  opacity: 0.9,
                }}
              >
                Loading your diabetes support system...
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.8,
                  fontStyle: "italic",
                  color: "white",
                }}
              >
                Made for the 5.5% of Australians
              </Typography>
            </Box>
          )}

          {/* Authentication and main app */}
          {!authChecking && (
            <>
              {/* Show password reset flow if requested */}
              {showPasswordReset && (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="100vh"
                  sx={{ background: "#005EB8" }}
                >
                  <PasswordResetFlow
                    onBackToSignIn={() => {
                      console.log(
                        "🔐 Returning to sign in from password reset",
                      );
                      setShowPasswordReset(false);
                    }}
                    onSuccess={() => {
                      console.log(
                        "🔐 Password reset successful, returning to sign in",
                      );
                      setShowPasswordReset(false);
                      setNotification({
                        open: true,
                        message:
                          "Password reset successful! You can now sign in with your new password.",
                        severity: "success",
                      });
                    }}
                  />
                </Box>
              )}

              {/* Add password requirements enhancer outside Authenticator */}
              <React.Suspense fallback={null}>
                <AmplifyPasswordEnhancer />
              </React.Suspense>

              {/* Only render Authenticator when NOT in password reset mode */}
              {!showPasswordReset && (
                <Authenticator
                  formFields={formFields}
                  components={authenticatorComponents}
                  loginMechanisms={["email"]}
                  signUpAttributes={["email"]}
                >
                  {({ signOut, user: authUser }) => {
                    // Authenticator render - minimal logging
                    if (process.env.NODE_ENV === "development") {
                      console.log(
                        "🔄 Auth State:",
                        appReady ? "Ready" : "Loading",
                      );
                    }

                    // Handle fresh login - fetch complete user attributes
                    if (authUser && !user) {
                      console.log(
                        "🔑 Fresh login detected, fetching complete user info...",
                      );
                      getCognitoUserWithAttributes()
                        .then((completeUser) => {
                          console.log(
                            "✅ Complete user info fetched on login:",
                            completeUser.fullName || "No name",
                          );
                          setUser(completeUser);
                        })
                        .catch((error) => {
                          console.error(
                            "❌ Error fetching user attributes, using basic info:",
                            error,
                          );
                          // Fallback to basic info if attribute fetch fails
                          const basicUserInfo = {
                            userId: authUser.userId || authUser.username,
                            username: authUser.username,
                            email:
                              authUser.signInDetails?.loginId ||
                              authUser.attributes?.email ||
                              "N/A",
                          };
                          setUser(basicUserInfo);
                        });
                    }

                    // Show login screen if not authenticated
                    if (!authUser) {
                      console.log(
                        "🔐 No authenticated user, showing login screen",
                      );
                      return null;
                    }

                    // Handle fresh login - fetch complete user attributes
                    if (authUser && !user) {
                      console.log(
                        "🔑 Fresh login detected (duplicate check), fetching complete user info...",
                      );
                      getCognitoUserWithAttributes()
                        .then((completeUser) => {
                          console.log(
                            "✅ Complete user info fetched on login (duplicate):",
                            completeUser.fullName || "No name",
                          );
                          setUser(completeUser);
                        })
                        .catch((error) => {
                          console.error(
                            "❌ Error fetching user attributes (duplicate), using basic info:",
                            error,
                          );
                          // Fallback to basic info if attribute fetch fails
                          const basicUserInfo = {
                            userId: authUser.userId || authUser.username,
                            username: authUser.username,
                            email:
                              authUser.signInDetails?.loginId ||
                              authUser.attributes?.email ||
                              "N/A",
                          };
                          setUser(basicUserInfo);
                        });
                    }

                    // Show login screen if not authenticated
                    if (!authUser) {
                      console.log(
                        "🔐 No authenticated user, showing login screen",
                      );
                      return null;
                    }

                    // REMOVED: Warmup screen (simplified approach)

                    // Show onboarding if needed
                    if (needsOnboarding && !checkingOnboarding) {
                      console.log(
                        "📋 Showing onboarding flow with user type selection",
                      );
                      return (
                        <React.Suspense
                          fallback={
                            <LoadingFallback
                              variant="minimal"
                              message="Loading onboarding..."
                            />
                          }
                        >
                          <OnboardingRouter
                            cognitoUser={user}
                            onComplete={() => {
                              console.log(
                                "✅ Onboarding completed, proceeding to app...",
                              );
                              setNeedsOnboarding(false);
                              // Reset initialization flag so app can initialize
                              initializationInProgress.current = false;
                              // App will initialize automatically via useEffect
                            }}
                          />
                        </React.Suspense>
                      );
                    }

                    // Show loading while checking onboarding
                    if (checkingOnboarding) {
                      return (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          minHeight="100vh"
                          sx={{ background: "#005EB8" }}
                        >
                          <Box textAlign="center">
                            <CircularProgress
                              size={48}
                              sx={{ color: "white", mb: 2 }}
                            />
                            <Typography variant="body1" sx={{ color: "white" }}>
                              Setting up your profile...
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }

                    // Main app interface
                    if (appReady) {
                      return (
                        <Router>
                          <Routes>
                            <Route
                              path="/"
                              element={
                                <Box
                                  display="flex"
                                  height="100vh"
                                  width="100vw"
                                  bgcolor="background.default"
                                  className="app-main-content"
                                  sx={{
                                    overflow: "hidden",
                                    position: "relative",
                                    transform: "none !important",
                                    zoom: "1 !important",
                                    minWidth: "100%",
                                    maxWidth: "100%",
                                  }}
                                >
                                  <a
                                    href="#main-content"
                                    style={{
                                      position: "absolute",
                                      left: "-9999px",
                                      zIndex: 999,
                                      padding: "8px 16px",
                                      background: "#000",
                                      color: "#fff",
                                      textDecoration: "none",
                                      borderRadius: "4px",
                                    }}
                                    onFocus={(e) => {
                                      e.target.style.left = "16px";
                                      e.target.style.top = "16px";
                                    }}
                                    onBlur={(e) => {
                                      e.target.style.left = "-9999px";
                                    }}
                                  >
                                    Skip to main content
                                  </a>

                                  <React.Suspense
                                    fallback={
                                      <LoadingFallback
                                        variant="minimal"
                                        message="Loading sidebar..."
                                      />
                                    }
                                  >
                                    <Sidebar
                                      sessions={sessions}
                                      currentSession={currentSession}
                                      onSessionSelect={handleSessionSelect}
                                      onSessionCreate={handleSessionCreate}
                                      onSessionUpdate={handleSessionUpdate}
                                      onSessionDelete={handleSessionDelete}
                                      onSignOut={signOut}
                                      user={user}
                                      mobileDrawerOpen={mobileDrawerOpen}
                                      onMobileDrawerToggle={
                                        handleMobileDrawerToggle
                                      }
                                    />
                                  </React.Suspense>

                                  <Box
                                    component="main"
                                    sx={{
                                      flexGrow: 1,
                                      display: "flex",
                                      flexDirection: "column",
                                      height: "100vh",
                                      width: "100%",
                                      minWidth: 0,
                                      overflow: "hidden",
                                      transform: "none !important",
                                      zoom: "1 !important",
                                      transition: "margin-left 0.3s ease",
                                    }}
                                    id="main-content"
                                  >
                                    <React.Suspense
                                      fallback={
                                        <LoadingFallback variant="chat" />
                                      }
                                    >
                                      <ChatInterface
                                        currentSession={currentSession}
                                        onSessionUpdate={handleSessionUpdate}
                                        onMobileDrawerToggle={
                                          handleMobileDrawerToggle
                                        }
                                      />
                                    </React.Suspense>
                                  </Box>

                                  {/* Early Access Alert */}
                                  <React.Suspense fallback={null}>
                                    <EarlyAccessAlert
                                      open={showEarlyAccessAlert}
                                      onClose={handleEarlyAccessAlertClose}
                                    />
                                  </React.Suspense>

                                  {/* Feedback Alert */}
                                  <React.Suspense fallback={null}>
                                    <FeedbackAlert
                                      open={showFeedbackAlert}
                                      onClose={handleFeedbackAlertClose}
                                      onNotNow={handleFeedbackAlertNotNow}
                                    />
                                  </React.Suspense>
                                </Box>
                              }
                            />
                            <Route
                              path="/profile"
                              element={
                                <React.Suspense
                                  fallback={
                                    <LoadingFallback variant="profile" />
                                  }
                                >
                                  <UserProfile user={user} />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="/tools"
                              element={
                                <React.Suspense
                                  fallback={
                                    <LoadingFallback
                                      variant="minimal"
                                      message="Loading tools..."
                                    />
                                  }
                                >
                                  <ToolsCenter cognitoUser={user} />
                                </React.Suspense>
                              }
                            />
                            <Route
                              path="/onboarding"
                              element={
                                <React.Suspense
                                  fallback={
                                    <LoadingFallback
                                      variant="minimal"
                                      message="Loading onboarding..."
                                    />
                                  }
                                >
                                  <OnboardingRouter
                                    cognitoUser={user}
                                    onComplete={() => {
                                      console.log(
                                        "✅ Onboarding updated, returning to app...",
                                      );
                                      // Update state to indicate onboarding is complete
                                      setNeedsOnboarding(false);
                                      // Navigate back to home after completion
                                      window.location.href = "/";
                                    }}
                                  />
                                </React.Suspense>
                              }
                            />
                          </Routes>
                        </Router>
                      );
                    }

                    // Fallback loading state
                    console.log("⏳ Showing fallback loading state");
                    return (
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="100vh"
                        sx={{ background: "#005EB8" }}
                      >
                        <Box textAlign="center">
                          <CircularProgress
                            size={48}
                            sx={{ color: "white", mb: 2 }}
                          />
                          <Typography variant="body1" sx={{ color: "white" }}>
                            Preparing your Diabetes AI Assistant...
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                </Authenticator>
              )}
            </>
          )}
        </Box>

        <Snackbar
          open={notification?.open}
          autoHideDuration={6000}
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleNotificationClose}
            severity={notification?.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {notification?.message}
          </Alert>
        </Snackbar>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
